import { SenderKindDoc, type SenderKindView } from "@kwp/contracts";
import { paths } from "@kwp/firestore";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import { Button, ErrorText, PageHeader, Section } from "../../ui";
import { RemoveSenderKindDialog, SENDER_KIND_LABELS } from "../inbox/SenderKind";
import { SettingsNav } from "./SettingsNav";

/**
 * Settings → Senders (architecture §6.7, WP-063): the person's own sender corrections, read with a
 * listener on `senderKinds` (rules let the owner read their own; nobody writes from here). Remove
 * shows what moves back first, then applies it through the api.
 */
export function useSenderKinds(): { senders: SenderKindView[] | null; error: boolean } {
  const { firebase } = useClients();
  const me = useMe();
  const [senders, setSenders] = useState<SenderKindView[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(
    () =>
      onSnapshot(
        query(collection(firebase.db, paths.senderKinds()), where("ownerUid", "==", me.uid)),
        (snap) => {
          setError(false);
          setSenders(
            snap.docs
              .flatMap((d) => {
                const parsed = SenderKindDoc.safeParse({ ...d.data(), createdAt: toDate(d.get("createdAt")) });
                return parsed.success
                  ? [
                      {
                        id: d.id,
                        match: parsed.data.match,
                        value: parsed.data.value,
                        kind: parsed.data.kind,
                        suggested: parsed.data.suggested,
                        createdAt: parsed.data.createdAt,
                      },
                    ]
                  : [];
              })
              .sort((a, b) => a.value.localeCompare(b.value)),
          );
        },
        () => setError(true),
      ),
    [firebase.db, me.uid],
  );
  return { senders, error };
}

function toDate(v: unknown): Date | null {
  return v !== null && typeof v === "object" && "toDate" in v && typeof v.toDate === "function" ? (v.toDate() as Date) : null;
}

export function SendersPage() {
  const { senders, error } = useSenderKinds();
  const [removing, setRemoving] = useState<SenderKindView | null>(null);
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Senders" />
      <SettingsNav />
      <Section
        title="Where senders' mail goes"
        description={`Your choices from "This sender is…": they put a sender's past and future mail in People, Notifications, Newsletters or Receipts, above anything its headers say. An address wins over its domain.`}
      >
        {error ? <ErrorText>Your senders could not be loaded.</ErrorText> : null}
        {senders === null && !error ? <p className="text-sm text-muted">Loading…</p> : null}
        {senders !== null && senders.length === 0 ? <p className="text-sm text-muted">No choices yet. Use "This sender is…" on a conversation.</p> : null}
        {senders === null || senders.length === 0 ? null : (
          <ul aria-label="Senders" className="divide-y divide-border">
            {senders.map((s) => (
              <li key={s.id} className="flex min-h-11 items-center gap-3 py-1 text-sm">
                <span className="min-w-0 flex-1 break-all text-text">{s.match === "domain" ? `Everyone at ${s.value}` : s.value}</span>
                <span className="shrink-0 text-muted">
                  {s.kind === "person" ? "People" : SENDER_KIND_LABELS[s.kind]}
                  {s.suggested ? " (Claude's suggestion)" : ""}
                </span>
                <Button variant="ghost" aria-label={`Remove ${s.value}`} onClick={() => setRemoving(s)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      {removing === null ? null : <RemoveSenderKindDialog match={removing.match} value={removing.value} onClose={() => setRemoving(null)} />}
    </div>
  );
}
